import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslate, useNotify, useGetList } from 'react-admin';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  Stack,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import QuickreplyIcon from '@mui/icons-material/Quickreply';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import type { Message } from '../../../types/chat.types';
import { EmojiReactionPicker } from '../EmojiReactionPicker';

// Accepted file types for attachments
const ACCEPTED_FILE_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  audio: ['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/webm'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/plain',
    'text/csv',
  ],
};

const ALL_ACCEPTED_TYPES = [
  ...ACCEPTED_FILE_TYPES.image,
  ...ACCEPTED_FILE_TYPES.video,
  ...ACCEPTED_FILE_TYPES.audio,
  ...ACCEPTED_FILE_TYPES.document,
].join(',');

interface SendMessagePayload {
  body: string;
  attachments?: File[];
  quotedMessageId?: number;
}

export interface MessageComposerProps {
  conversationUuid: string;
  onSendMessage: (message: SendMessagePayload) => Promise<void>;
  replyingTo?: Message | null;
  onCancelReply?: () => void;
  disabled?: boolean;
  onTyping?: () => void;
  onStopTyping?: () => void;
  maxFileSizeBytes?: number;
  providerName?: string;
}

interface FilePreview {
  file: File;
  previewUrl: string | null;
  type: 'image' | 'video' | 'audio' | 'document';
}

function getFileType(mimeType: string): FilePreview['type'] {
  if (ACCEPTED_FILE_TYPES.image.includes(mimeType)) return 'image';
  if (ACCEPTED_FILE_TYPES.video.includes(mimeType)) return 'video';
  if (ACCEPTED_FILE_TYPES.audio.includes(mimeType)) return 'audio';
  return 'document';
}

function FileTypeIcon({ type }: { type: FilePreview['type'] }) {
  const iconProps = { fontSize: 'small' as const };
  switch (type) {
    case 'image':
      return <ImageIcon {...iconProps} />;
    case 'video':
      return <VideoFileIcon {...iconProps} />;
    case 'audio':
      return <AudioFileIcon {...iconProps} />;
    default:
      return <InsertDriveFileIcon {...iconProps} />;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function MessageComposer({
  conversationUuid,
  onSendMessage,
  replyingTo,
  onCancelReply,
  disabled = false,
  onTyping,
  onStopTyping,
  maxFileSizeBytes,
  providerName,
}: MessageComposerProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [attachments, setAttachments] = useState<FilePreview[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const [quickReplyAnchorEl, setQuickReplyAnchorEl] = useState<HTMLElement | null>(null);
  const [quickReplySearch, setQuickReplySearch] = useState('');
  const [debouncedQuickReplySearch, setDebouncedQuickReplySearch] = useState('');
  const [slashQuery, setSlashQuery] = useState<string | null>(null);
  const [debouncedSlashQuery, setDebouncedSlashQuery] = useState<string | null>(null);
  const [slashSelectedIndex, setSlashSelectedIndex] = useState(0);
  const quickReplyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slashDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce quick reply popover search
  useEffect(() => {
    if (quickReplyDebounceRef.current) clearTimeout(quickReplyDebounceRef.current);
    quickReplyDebounceRef.current = setTimeout(() => {
      setDebouncedQuickReplySearch(quickReplySearch);
    }, 300);
    return () => { if (quickReplyDebounceRef.current) clearTimeout(quickReplyDebounceRef.current); };
  }, [quickReplySearch]);

  // Debounce slash query
  useEffect(() => {
    if (slashQuery === null) {
      setDebouncedSlashQuery(null);
      return;
    }
    if (slashDebounceRef.current) clearTimeout(slashDebounceRef.current);
    slashDebounceRef.current = setTimeout(() => {
      setDebouncedSlashQuery(slashQuery);
    }, 300);
    return () => { if (slashDebounceRef.current) clearTimeout(slashDebounceRef.current); };
  }, [slashQuery]);

  // Fetch quick replies for popover (server-side filtered)
  const isQuickReplyPopoverOpen = Boolean(quickReplyAnchorEl);
  const { data: popoverQuickReplies, isLoading: isLoadingPopover } = useGetList(
    'ticket-quick-reply',
    {
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'title', order: 'ASC' },
      filter: debouncedQuickReplySearch ? { title_q: debouncedQuickReplySearch } : {},
    },
    { enabled: isQuickReplyPopoverOpen },
  );

  // Fetch quick replies for slash menu (server-side filtered)
  const { data: slashReplies, isLoading: isLoadingSlash } = useGetList(
    'ticket-quick-reply',
    {
      pagination: { page: 1, perPage: 10 },
      sort: { field: 'shortcut', order: 'ASC' },
      filter: debouncedSlashQuery ? { shortcut_q: debouncedSlashQuery } : {},
    },
    { enabled: debouncedSlashQuery !== null },
  );

  const slashSuggestions = slashReplies ?? [];
  const isSlashMenuOpen = slashQuery !== null && slashSuggestions.length > 0;

  // Cleanup preview URLs on unmount or when attachments change
  useEffect(() => {
    return () => {
      attachments.forEach((attachment) => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
    };
  }, [attachments]);

  // Reset conversation-specific state when conversationUuid changes
  useEffect(() => {
    setInputValue('');
    setAttachments([]);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    onStopTyping?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on conversation change, not on callback change
  }, [conversationUuid]);

  // Handle typing indicator with debounce
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setInputValue(value);

      // Detect slash command trigger: "/" at start of input
      if (value.startsWith('/')) {
        setSlashQuery(value.slice(1));
        setSlashSelectedIndex(0);
      } else {
        setSlashQuery(null);
      }

      if (onTyping && value.length > 0) {
        onTyping();

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
          onStopTyping?.();
        }, 2000);
      } else if (value.length === 0) {
        onStopTyping?.();
      }
    },
    [onTyping, onStopTyping]
  );

  // Handle file selection
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      const newAttachments: FilePreview[] = [];
      const rejected: string[] = [];

      Array.from(files).forEach((file) => {
        if (maxFileSizeBytes && file.size > maxFileSizeBytes) {
          rejected.push(
            `${file.name} (${formatFileSize(file.size)})`,
          );
          return;
        }

        const fileType = getFileType(file.type);
        let previewUrl: string | null = null;

        if (fileType === 'image') {
          previewUrl = URL.createObjectURL(file);
        }

        newAttachments.push({
          file,
          previewUrl,
          type: fileType,
        });
      });

      if (rejected.length > 0) {
        notify(
          translate('resources.chat.fileTooLarge', {
            provider: providerName ?? translate('resources.chat.provider'),
            maxSize: formatFileSize(maxFileSizeBytes!),
            files: rejected.join(', '),
          }),
          { type: 'warning' },
        );
      }

      setAttachments((prev) => [...prev, ...newAttachments]);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [maxFileSizeBytes, providerName, notify, translate]
  );

  // Remove attachment
  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => {
      const attachment = prev[index];
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // Open file picker
  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Emoji picker
  const handleEmojiClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  }, []);

  const handleEmojiSelect = useCallback((emoji: string) => {
    if (!emoji) return;
    const textarea = inputRef.current;
    if (textarea) {
      const start = textarea.selectionStart ?? inputValue.length;
      const end = textarea.selectionEnd ?? inputValue.length;
      const newValue = inputValue.slice(0, start) + emoji + inputValue.slice(end);
      setInputValue(newValue);
      // Restore cursor position after emoji
      requestAnimationFrame(() => {
        textarea.focus();
        const cursorPos = start + emoji.length;
        textarea.setSelectionRange(cursorPos, cursorPos);
      });
    } else {
      setInputValue((prev) => prev + emoji);
    }
  }, [inputValue]);

  // Quick reply picker
  const handleQuickReplyClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setQuickReplyAnchorEl(event.currentTarget);
    setQuickReplySearch('');
  }, []);

  const handleQuickReplySelect = useCallback((content: string) => {
    setInputValue(content);
    setQuickReplyAnchorEl(null);
    setSlashQuery(null);
    inputRef.current?.focus();
  }, []);

  // Paste handler for images from clipboard
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            if (maxFileSizeBytes && file.size > maxFileSizeBytes) {
              notify(
                translate('resources.chat.fileTooLarge', {
                  provider: providerName ?? translate('resources.chat.provider'),
                  maxSize: formatFileSize(maxFileSizeBytes),
                  files: file.name || translate('resources.chat.pasteImage'),
                }),
                { type: 'warning' },
              );
              continue;
            }
            imageFiles.push(file);
          }
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        const newAttachments: FilePreview[] = imageFiles.map((file) => ({
          file,
          previewUrl: URL.createObjectURL(file),
          type: 'image' as const,
        }));
        setAttachments((prev) => [...prev, ...newAttachments]);
      }
    },
    [maxFileSizeBytes, providerName, notify, translate]
  );

  // Send message
  const handleSend = useCallback(async () => {
    const trimmedValue = inputValue.trim();
    const hasContent = trimmedValue.length > 0 || attachments.length > 0;

    if (!hasContent || isSending || disabled) return;

    setIsSending(true);
    onStopTyping?.();

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      await onSendMessage({
        body: trimmedValue,
        attachments: attachments.length > 0 ? attachments.map((a) => a.file) : undefined,
        quotedMessageId: replyingTo?.id,
      });

      // Clear state on success
      setInputValue('');
      attachments.forEach((attachment) => {
        if (attachment.previewUrl) {
          URL.revokeObjectURL(attachment.previewUrl);
        }
      });
      setAttachments([]);
      onCancelReply?.();
      inputRef.current?.focus();
    } catch {
      notify(translate('resources.chat.sendError'), { type: 'error' });
    } finally {
      setIsSending(false);
    }
  }, [
    inputValue,
    attachments,
    isSending,
    disabled,
    onSendMessage,
    replyingTo,
    onCancelReply,
    onStopTyping,
  ]);

  // Handle Enter key (send) and Shift+Enter (new line)
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Slash menu keyboard navigation
      if (isSlashMenuOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSlashSelectedIndex((prev) => Math.min(prev + 1, slashSuggestions.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSlashSelectedIndex((prev) => Math.max(prev - 1, 0));
          return;
        }
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          const selected = slashSuggestions[slashSelectedIndex];
          if (selected) {
            handleQuickReplySelect(selected.content);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setSlashQuery(null);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, isSlashMenuOpen, slashSuggestions, slashSelectedIndex, handleQuickReplySelect]
  );

  // Check if send button should be enabled
  const canSend = useMemo(() => {
    const hasContent = inputValue.trim().length > 0 || attachments.length > 0;
    return hasContent && !isSending && !disabled;
  }, [inputValue, attachments.length, isSending, disabled]);

  // Get sender name for reply quote
  const getReplySenderName = useCallback(
    (msg: Message) =>
      msg.senderUser?.name ||
      msg.senderUser?.email ||
      msg.senderContactIdentity?.name ||
      translate('resources.chat.unknownSender'),
    [translate]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Reply quote */}
      {replyingTo && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: 'action.hover',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              flex: 1,
              pl: 1.5,
              borderLeft: 3,
              borderLeftColor: 'primary.main',
              overflow: 'hidden',
            }}
          >
            <Typography variant="caption" color="primary" fontWeight="bold">
              {getReplySenderName(replyingTo)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ maxWidth: '100%' }}
            >
              {replyingTo.body || `[${translate('resources.chat.attachment')}]`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onCancelReply} aria-label={translate('ra.action.close')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Attachment previews */}
      {attachments.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            px: 2,
            py: 1,
            overflowX: 'auto',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {attachments.map((attachment, index) => (
            <Paper
              key={`${attachment.file.name}-${index}`}
              variant="outlined"
              sx={{
                position: 'relative',
                flexShrink: 0,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                maxWidth: 200,
              }}
            >
              {attachment.type === 'image' && attachment.previewUrl ? (
                <Box
                  component="img"
                  src={attachment.previewUrl}
                  alt={attachment.file.name}
                  sx={{
                    width: 48,
                    height: 48,
                    objectFit: 'cover',
                    borderRadius: 0.5,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'action.selected',
                    borderRadius: 0.5,
                  }}
                >
                  <FileTypeIcon type={attachment.type} />
                </Box>
              )}
              <Box sx={{ overflow: 'hidden', flex: 1 }}>
                <Typography variant="caption" noWrap title={attachment.file.name}>
                  {attachment.file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {formatFileSize(attachment.file.size)}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemoveAttachment(index)}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'background.paper',
                  boxShadow: 1,
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
                aria-label={translate('ra.action.remove')}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ))}
        </Box>
      )}

      {/* Slash command suggestions */}
      {slashQuery !== null && (isSlashMenuOpen || isLoadingSlash) && (
        <Paper
          elevation={4}
          sx={{
            mx: 2,
            mb: 0.5,
            maxHeight: 200,
            overflowY: 'auto',
            borderRadius: 2,
          }}
        >
          {isLoadingSlash ? (
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <List dense sx={{ py: 0.5 }}>
              {slashSuggestions.map((qr, index) => (
                <ListItemButton
                  key={qr.id}
                  selected={index === slashSelectedIndex}
                  onClick={() => handleQuickReplySelect(qr.content)}
                  sx={{ py: 0.75 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label={qr.shortcut} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                        <Typography variant="body2">{qr.title}</Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {qr.content}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>
      )}

      {/* Input area */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          p: 2,
        }}
      >
        {/* Attachment button */}
        <IconButton
          size="small"
          onClick={handleAttachClick}
          disabled={disabled || isSending}
          aria-label={translate('resources.chat.attachFile')}
        >
          <AttachFileIcon />
        </IconButton>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALL_ACCEPTED_TYPES}
          multiple
          hidden
          onChange={handleFileSelect}
        />

        {/* Emoji button */}
        <IconButton
          size="small"
          onClick={handleEmojiClick}
          disabled={disabled || isSending}
          aria-label={translate('resources.chat.addEmoji')}
        >
          <InsertEmoticonIcon />
        </IconButton>

        {/* Emoji picker popover */}
        <EmojiReactionPicker
          anchorEl={emojiAnchorEl}
          open={Boolean(emojiAnchorEl)}
          onClose={() => setEmojiAnchorEl(null)}
          onSelect={handleEmojiSelect}
          keepOpenOnSelect
        />

        {/* Quick replies button */}
        <IconButton
          size="small"
          onClick={handleQuickReplyClick}
          disabled={disabled || isSending}
          aria-label={translate('resources.chat.quickReplies')}
        >
          <QuickreplyIcon />
        </IconButton>

        {/* Quick replies popover */}
        <Popover
          open={Boolean(quickReplyAnchorEl)}
          anchorEl={quickReplyAnchorEl}
          onClose={() => setQuickReplyAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          slotProps={{
            paper: {
              sx: { width: 320, maxHeight: 400, display: 'flex', flexDirection: 'column' },
            },
          }}
        >
          <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={translate('resources.chat.quickReplySearch')}
              value={quickReplySearch}
              onChange={(e) => setQuickReplySearch(e.target.value)}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List sx={{ overflowY: 'auto', flex: 1, py: 0 }}>
            {isLoadingPopover ? (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : !popoverQuickReplies?.length ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.chat.noQuickReplies')}
                </Typography>
              </Box>
            ) : (
              popoverQuickReplies.map((qr) => (
                <ListItemButton
                  key={qr.id}
                  onClick={() => handleQuickReplySelect(qr.content)}
                  sx={{ py: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {qr.title}
                        </Typography>
                        {qr.shortcut && (
                          <Chip label={qr.shortcut} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {qr.content}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Popover>

        {/* Text input */}
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={5}
          placeholder={translate('resources.chat.typeMessage')}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled || isSending}
          size="small"
          aria-label={translate('resources.chat.messageInput')}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            },
          }}
        />

        {/* Send button */}
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!canSend}
          aria-label={translate('ra.action.send')}
        >
          {isSending ? <CircularProgress size={24} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}
